import store from '@/store'
import Project from '@/modules/project/views/project.vue'
import Vue from 'vue'
import VueRouter from 'vue-router'
import moment from 'moment-timezone'
import { getDefaultShiftsEndDate } from '@/modules/shifts/utils/shifts-default-filter.js'

Vue.use(VueRouter)

const normalizeToArray = param => (Array.isArray(param) ? param : param ? [param] : undefined)
const getAssociateProfilesFilters = query => {
  const associates = normalizeToArray(query.associates)
  const hasAssociates = Boolean(associates?.length)

  return {
    roles: hasAssociates ? undefined : normalizeToArray(query.roles),
    associates,
    unverified: query.unverified,
    departments: hasAssociates ? undefined : normalizeToArray(query.departments),
    legal_entities: hasAssociates ? undefined : normalizeToArray(query.legal_entities),
    payment_types: hasAssociates ? undefined : normalizeToArray(query.payment_types),
    statuses: hasAssociates ? undefined : normalizeToArray(query.statuses ?? ['active', 'suspended'])
  }
}
const getTimeOffFilters = query => {
  const associates = normalizeToArray(query.associates)
  const hasAssociates = Boolean(associates?.length)
  const defaultValues = {
    statuses: ['1', '2']
  }

  return {
    associates,
    statuses: normalizeToArray(query.statuses ?? defaultValues.statuses),
    types: normalizeToArray(query.types),
    roles: hasAssociates ? undefined : normalizeToArray(query.roles),
    payment_types: hasAssociates ? undefined : normalizeToArray(query.payment_types),
    departments: hasAssociates ? undefined : normalizeToArray(query.departments),
    end: query.end,
    start: query.start
  }
}
const getAssociateLeaderboardFilters = query => {
  const associates = normalizeToArray(query.associates)
  const hasAssociates = Boolean(associates?.length)

  return {
    roles: hasAssociates ? undefined : normalizeToArray(query.roles),
    associates,
    unverified: query.unverified,
    departments: hasAssociates ? undefined : normalizeToArray(query.departments),
    legal_entities: hasAssociates ? undefined : normalizeToArray(query.legal_entities),
    payment_types: hasAssociates ? undefined : normalizeToArray(query.payment_types),
    point_system_action_group: normalizeToArray(query.point_system_action_group),
    point_system_action_status: normalizeToArray(query.point_system_action_status)
  }
}
const Auth = () => import('@/views/authorization/auth.vue')
const TimeCards = () => import('../modules/shifts/views/shifts.vue')
const PayCodes = () => import('../modules/paycode/views/pay-codes.vue')
const Visitors = () => import('../views/visitors.vue')
const TimeOff = () => import('@/modules/time-off/views/time-off.vue')
const ProjectShiftAuths = () => import('@/modules/authorizations/views/shift-authorizations.vue')
const ProjectShiftAuthsPage = () => import('@/modules/authorizations/views/shift-authorizations-page.vue')
const ProjectDisciplinaryActions = () => import('@/modules/disciplinary/components/disciplinary-actions-table.vue')
const AssociateProfiles = () => import('@/modules/profile/views/associate-profiles.vue')
const AlertStore = () => import('@/modules/alerts/views/alert-store.vue')
const GeneralDailyReport = () => import('@/modules/report/views/daily-summary.vue')
const ConfigurationPage = () => import('@/modules/project/views/configuration-page.vue')

const routes = [
  {
    path: '*',
    redirect: '/'
  },
  {
    path: '/',
    name: 'home',
    component: () => import('../modules/project/views/dashboard.vue'),
    props: true,
    meta: { title: 'Dashboard - ePeople' }
  },
  {
    path: '/configuration-page',
    name: 'configuration-page',
    component: ConfigurationPage,
    props: true,
    redirect: { name: 'configuration-page.info' },
    children: [
      {
        path: 'info',
        component: Project,
        props: true,
        meta: { title: 'Information - ePeople' },
        name: 'configuration-page.info'
      },
      {
        path: 'settings',
        component: () => import('../modules/project/views/settings.vue'),
        meta: {
          title: 'Settings - ePeople'
        },
        name: 'configuration-page.settings'
      },
      {
        path: 'payroll-settings',
        component: () => import('../modules/project/views/payroll-settings.vue'),
        meta: {
          title: 'Payroll Settings - ePeople'
        },
        name: 'configuration-page.payroll-settings'
      },
      {
        path: 'pay-codes',
        component: PayCodes,
        props: true,
        meta: { title: 'Pay Codes - ePeople' },
        name: 'configuration-page.pay-codes'
      },
      {
        path: 'features',
        component: () => import('../modules/project/views/features.vue'),
        meta: {
          title: 'Company Features - ePeople'
        },
        name: 'configuration-page.features'
      },
      {
        path: 'day-positions',
        component: () => import('../modules/project/views/day-positions.vue'),
        meta: {
          title: 'Day Positions - ePeople'
        },
        name: 'configuration-page.day-positions'
      },
      {
        path: 'disciplinary-action',
        component: () => import('../modules/project/views/disciplinary-action.vue'),
        meta: {
          title: 'Time Constants - ePeople'
        },
        name: 'configuration-page.disciplinary-action'
      },
      {
        path: 'work-areas',
        component: () => import('../modules/work-area/views/work-areas.vue'),
        meta: {
          title: 'Work Areas - ePeople'
        },
        name: 'configuration-page.work-areas'
      },
      {
        path: 'roles',
        component: () => import('../modules/roles/view/roles.vue'),
        props: true,
        meta: { title: 'Positions - ePeople' },
        name: 'configuration-page.roles'
      },
      {
        path: 'departments',
        component: () => import('../modules/department/views/departments.vue'),
        props: true,
        meta: { title: 'Departments - ePeople' },
        name: 'configuration-page.departments'
      },
      {
        path: 'terminal-user',
        component: () => import('../modules/user/views/terminal-user.vue'),
        meta: {
          title: 'Terminal User - ePeople'
        },
        name: 'configuration-page.terminal-user'
      },
      {
        path: 'wallets',
        component: () => import('../modules/payment-system/views/payment-systems.vue'),
        meta: {
          title: 'Stripe - ePeople',
          permissions: ['validate_payments', 'process_payments']
        },
        name: 'configuration-page.wallets'
      }
    ]
  },
  {
    path: '/kpi',
    name: 'kpi',
    component: () => import('../modules/project/views/kpi.vue'),
    props: true,
    meta: { title: 'KPI - ePeople' }
  },
  {
    path: '/no-permissions',
    name: 'no-permissions',
    component: () => import('../views/no-permissions.vue'),
    meta: { title: 'ePeople' }
  },
  {
    path: '/error',
    name: 'error',
    component: () => import('../views/error.vue'),
    props: route => ({
      errorType: route.params.errorType || 'server-unavailable',
      errorCode: route.params.errorCode || '503',
      errorTitle: route.params.errorTitle,
      errorDescription: route.params.errorDescription
    }),
    meta: { public: true, title: 'Error - ePeople' }
  },
  {
    path: '/stripe-v2/onboarding/return',
    name: 'stripe-v2.onboarding-return',
    component: () => import('../modules/payment/views/stripe-v2-onboarding-return.vue'),
    meta: { public: true, title: 'Stripe Setup - ePeople' }
  },
  {
    path: '/inactive-link',
    name: 'inactive-link',
    component: () => import('../views/error.vue'),
    props: {
      errorType: 'inactive-link'
    },
    meta: { title: 'Link Is No Longer Active - ePeople' }
  },
  {
    path: '/statistic',
    name: 'statistic',
    component: () => import('../modules/project/views/statistic.vue'),
    props: true,
    meta: {
      title: 'Statistic - ePeople',
      permissions: ['manage_project']
    }
  },
  {
    path: '/auth',
    name: 'auth',
    component: Auth,
    props: route => ({
      initialUsername: route.params.initialUsername
    }),
    meta: { public: true, title: 'ePeople' }
  },
  {
    path: '/sign-up',
    name: 'sign-up',
    component: () => import('../views/authorization/sign-up.vue'),
    props: true,
    meta: { public: true, title: 'Sign Up ePeople' }
  },
  {
    path: '/attach-files',
    name: 'attach-files',
    component: () => import('../views/attach-files.vue'),
    props: route => {
      return {
        associate_id: route.query.associate_id,
        shift_id: route.query.shift_id,
        created_at: route.query.created_at
      }
    },
    meta: { public: true, title: 'Attach Files - ePeople' }
  },
  {
    path: '/create-associate-document',
    name: 'create-associate-document',
    component: () => import('../views/create-associate-documents.vue'),
    props: route => {
      return {
        associate_id: route.query.associate_id,
        template_id: route.query.template_id,
        created_at: route.query.created_at
      }
    },
    meta: { public: true, title: 'Create Associate Document - ePeople' }
  },
  {
    path: '/shifts',
    name: 'shifts',
    component: TimeCards,
    props: route => {
      const defaultValues = {
        shift_statuses: ['deleted', 'missed', 'cancelled', 'completed', 'started', 'scheduled'],
        shift_payment_statuses: ['not_paid', 'paid', 'in_progress', 'partially_paid', 'draft', 'cancelled']
      }

      return {
        filterNew: {
          end_date: route.query.end_date ?? getDefaultShiftsEndDate(),
          start_date: route.query.start_date,
          roles: normalizeToArray(route.query.roles),
          associates: normalizeToArray(route.query.associates),
          shift_ids: normalizeToArray(route.query.shift_ids),
          shift_statuses: normalizeToArray(route.query.shift_statuses ?? defaultValues.shift_statuses),
          ready_for_pay_status: route.query.ready_for_pay_status,
          auth_types: normalizeToArray(route.query.auth_types),
          shift_payment_statuses: normalizeToArray(
            route.query.shift_payment_statuses ?? defaultValues.shift_payment_statuses
          ),
          not_ready_reasons: normalizeToArray(route.query.not_ready_reasons),
          payment_types: normalizeToArray(route.query.payment_types),
          shift_type: route.query.shift_type,
          manually_created: route.query.manually_created,
          training_period: route.query.training_period,
          zero_rate: route.query.zero_rate,
          orientation_period: route.query.orientation_period,
          with_pay_codes: route.query.with_pay_codes,
          calculation_types: normalizeToArray(route.query.calculation_types),
          pay_codes: normalizeToArray(route.query.pay_codes),
          attached_pay_code: route.query.attached_pay_code,
          adjusted: route.query.adjusted,
          staffing: route.query.staffing,
          origins: normalizeToArray(route.query.origins),
          facility_project_ids: normalizeToArray(route.query.facility_project_ids),
          associate_statuses: normalizeToArray(route.query.associate_statuses),
          legal_entities: normalizeToArray(route.query.legal_entities),
          departments: normalizeToArray(route.query.departments),
          schedule_types: normalizeToArray(route.query.schedule_types),
          pay_statuses: normalizeToArray(route.query.pay_statuses)
        },
        shift_ids: normalizeToArray(route.query.shift_ids)
      }
    },
    meta: {
      title: 'Associate Shifts - ePeople',
      permissions: ['manage_shifts']
    }
  },

  {
    path: '/associate-pay-codes',
    name: 'associate-pay-codes',
    component: () => import('../modules/shifts/views/shifts-pay-codes.vue'),
    props: route => {
      return {
        filters: {
          start: route.query.start,
          end: route.query.end,
          associate_number: route.query.associate_number
        }
      }
    },
    meta: {
      title: 'Associate Pay Codes - ePeople',
      permissions: ['manage_shifts']
    }
  },

  {
    path: '/invoices',
    name: 'invoices',
    component: () => import('../modules/payment/views/payments.vue'),
    props: route => {
      return {
        filters: {
          start: route.query.start,
          end: route.query.end,
          associates: normalizeToArray(route.query.associates),
          legal_entities: normalizeToArray(route.query.legal_entities),
          statuses: normalizeToArray(route.query.statuses),
          system: route.query.system,
          history: route.query.history
        },
        type: 'invoices'
      }
    },
    meta: {
      title: 'Invoices - ePeople',
      permissions: ['validate_payments', 'process_payments']
    }
  },
  {
    path: '/advances',
    name: 'advances',
    component: () => import('../modules/payment/views/payments.vue'),
    props: route => {
      return {
        filters: {
          start: route.query.start,
          end: route.query.end,
          associates: normalizeToArray(route.query.associates),
          statuses: normalizeToArray(route.query.statuses),
          system: route.query.system,
          history: route.query.history
        },
        type: 'advances'
      }
    },
    meta: {
      title: 'Advances - ePeople',
      permissions: ['validate_payments', 'process_payments']
    }
  },
  {
    path: '/clients-request',
    name: 'clients-request',
    component: () => import('../modules/payment/views/payments.vue'),
    props: route => {
      return {
        filters: {
          start: route.query.start,
          end: route.query.end,
          associates: normalizeToArray(route.query.associates),
          legal_entities: normalizeToArray(route.query.legal_entities),
          statuses: normalizeToArray(route.query.statuses),
          facility_project_ids: normalizeToArray(route.query.facility_project_ids),
          system: route.query.system,
          history: route.query.history
        },
        type: 'clients'
      }
    },
    meta: {
      title: 'Client Requests - ePeople',
      permissions: ['validate_payments', 'process_payments']
    }
  },
  {
    path: '/company-invoice',
    name: 'company-invoice',
    component: () => import('../modules/payment/views/payments.vue'),
    props: route => {
      return {
        filters: {
          start: route.query.start,
          end: route.query.end,
          associates: normalizeToArray(route.query.associates),
          legal_entities: normalizeToArray(route.query.legal_entities),
          statuses: normalizeToArray(route.query.statuses),
          system: route.query.system,
          history: route.query.history
        },
        type: 'company-invoice'
      }
    },
    meta: {
      title: 'Company Invoices - ePeople',
      permissions: ['validate_payments', 'process_payments']
    }
  },
  {
    path: '/payments',
    name: 'payments',
    component: () => import('../modules/payment/views/payments.vue'),
    props: route => {
      return {
        filters: {
          start: route.query.start,
          end: route.query.end,
          associates: normalizeToArray(route.query.associates),
          legal_entities: normalizeToArray(route.query.legal_entities),
          statuses: normalizeToArray(route.query.statuses),
          system: route.query.system,
          history: route.query.history,
          payment_types: normalizeToArray(route.query.payment_types),
          request_types: normalizeToArray(route.query.request_types)
        },
        type: 'history-payments',
        request_ids: normalizeToArray(route.query.request_ids)
      }
    },
    meta: {
      title: 'Payments - ePeople',
      permissions: ['validate_payments', 'process_payments']
    }
  },
  {
    path: '/clients-request-history',
    name: 'clients-request-history',
    component: () => import('../modules/payment/views/payments.vue'),
    props: route => {
      return {
        filters: {
          start: route.query.start,
          end: route.query.end,
          associates: normalizeToArray(route.query.associates),
          legal_entities: normalizeToArray(route.query.legal_entities),
          statuses: normalizeToArray(route.query.statuses),
          system: route.query.system,
          history: route.query.history,
          payment_types: normalizeToArray(route.query.payment_types)
        },
        type: 'history-clients',
        request_ids: normalizeToArray(route.query.request_ids)
      }
    },
    meta: {
      title: 'Payment History - ePeople',
      permissions: ['validate_payments', 'process_payments']
    }
  },
  {
    path: '/legal-entity',
    name: 'legal-entity',
    component: () => import('../modules/legal-entity/views/legal-entity.vue'),
    props: route => {
      return {
        filters: {
          id: normalizeToArray(route.query.id),
          type: route.query.type,
          tax_id: route.query.tax_id
        }
      }
    },
    meta: { title: 'Legal Entities - ePeople' }
  },
  {
    path: '/active-associates',
    name: 'active-associates',
    component: () => import('../views/active-associates.vue'),
    props: true,
    meta: {
      title: 'Active Associates - ePeople',
      permissions: ['manage_associates']
    }
  },
  {
    path: '/time-attendance-detail-report',
    name: 'time-attendance-detail-report',
    component: () => import('../modules/report/views/time-attendance-detail-report.vue'),
    props: true,
    meta: { title: 'Time Attendance Detail Report - ePeople' }
  },
  {
    path: '/documentation-compliance-report',
    name: 'documentation-compliance-report',
    component: () => import('../modules/report/views/documentation.vue'),
    props: true,
    meta: { title: 'Documentation Report - ePeople' }
  },
  {
    path: '/pbj-report',
    name: 'pbj-report',
    component: () => import('../modules/report/views/pbj-report.vue'),
    props: route => {
      return {
        filters: {
          facility_id: route.query.facility_id,
          start_date:
            route.query.start_date ??
            moment().utc().tz(store.state.applicationState.timeZone).format(store.state.applicationState.dateFormat),
          end_date:
            route.query.end_date ??
            moment().utc().tz(store.state.applicationState.timeZone).format(store.state.applicationState.dateFormat),
          selected_pay_types: normalizeToArray(route.query.selected_pay_types ?? [1, 2, 3])
        }
      }
    },
    meta: { title: 'PBJ & PPD - ePeople' }
  },
  {
    path: '/pbj-audit/drafts/:draftId',
    name: 'pbj-audit-draft',
    component: () => import('../modules/report/views/pbj-audit-draft.vue'),
    props: route => ({
      draftId: Number(route.params.draftId)
    }),
    meta: { title: 'PBJ Audit Draft - ePeople' }
  },
  {
    path: '/cross-system-report',
    name: 'cross-system-report',
    component: () => import('../modules/report/views/cross-system.vue'),
    props: true,
    meta: { title: 'Cross-System Compliance Report - ePeople' }
  },
  {
    path: '/discrepancy-report',
    name: 'discrepancy-report',
    component: () => import('../modules/report/views/discrepancy.vue'),
    props: true,
    meta: { title: 'Discrepancy Report - ePeople' }
  },
  {
    path: '/high-risk-report',
    name: 'high-risk-report',
    component: () => import('../modules/report/views/high-risk.vue'),
    props: route => ({
      hasLimit: true
    }),
    meta: { title: 'High Risk Associates - ePeople' }
  },
  {
    path: '/unplanned-cost-report',
    name: 'unplanned-cost-report',
    component: () => import('../modules/report/views/high-risk.vue'),
    props: true,
    meta: { title: 'Unplanned Cost - ePeople' }
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('../modules/notifications/views/notifications.vue'),
    props: true,
    meta: {
      title: 'Notifications - ePeople',
      permissions: ['send_push_notification']
    }
  },
  {
    path: '/visitors',
    name: 'visitors',
    component: Visitors,
    props: true,
    meta: { title: 'Visitors - ePeople' }
  },
  {
    path: '/payroll',
    name: 'payroll',
    component: () => import('../modules/payroll/views/payroll.vue'),
    props: route => ({
      tab: route.query.tab ?? 'associates_table',
      id: route.query.id
    }),
    beforeEnter(to, from, next) {
      store.commit('payroll/resetState')
      next()
    },
    meta: {
      title: 'Payroll - ePeople',
      permissions: ['validate_payments', 'process_payments']
    }
  },
  {
    path: '/past-payrolls',
    name: 'past-payrolls',
    component: () => import('../modules/payroll/views/payroll-history.vue'),
    props: true,
    meta: {
      title: 'Payroll History - ePeople',
      permissions: ['validate_payments', 'process_payments']
    }
  },
  {
    path: '/project-shift-auths',
    name: 'project-shift-auths-page',
    component: ProjectShiftAuthsPage,
    redirect: { name: 'project-shift-auths' },
    children: [
      {
        path: 'violations',
        name: 'project-shift-auths',
        component: ProjectShiftAuths,
        props: route => {
          return {
            filters: {
              end: route.query.end,
              start: route.query.start,
              roles: normalizeToArray(route.query.roles),
              associates: normalizeToArray(route.query.associates),
              providers: normalizeToArray(route.query.providers),
              reviewers: normalizeToArray(route.query.reviewers),
              auth_types: normalizeToArray(route.query.auth_types),
              assigned_to_me: route.query.assigned_to_me,
              adjusted: route.query.adjusted,
              manually_created: route.query.manually_created,
              auth_statuses: normalizeToArray(route.query.auth_statuses),
              origins: normalizeToArray(route.query.origins),
              shift_payment_statuses: normalizeToArray(route.query.shift_payment_statuses),
              resolved_by_associate: route.query.resolved_by_associate,
              associate_statuses: normalizeToArray(route.query.associate_statuses),
              departments: normalizeToArray(route.query.departments),
              schedule_types: normalizeToArray(route.query.schedule_types),
              payment_types: normalizeToArray(route.query.payment_types),
              pay_statuses: normalizeToArray(route.query.pay_statuses),
              legal_entities: normalizeToArray(route.query.legal_entities),
              shift_statuses: normalizeToArray(route.query.shift_statuses),
              shift_type: route.query.shift_type,
              attached_pay_code: route.query.attached_pay_code,
              pay_codes: normalizeToArray(route.query.pay_codes)
            }
          }
        },
        meta: {
          title: 'Violations - ePeople'
        }
      },
      {
        path: 'disciplinary-actions',
        name: 'project-shift-auths.disciplinary-actions',
        component: ProjectDisciplinaryActions,
        props: route => ({
          filters: {
            associate_id: route.query.associate_id,
            start_date: route.query.start_date,
            end_date: route.query.end_date,
            types: normalizeToArray(route.query.types)
          },
          useRouter: true
        }),
        meta: {
          title: 'Disciplinary Actions - ePeople',
          permissions: ['manage_associates']
        }
      }
    ]
  },
  {
    path: '/associate-profiles',
    name: 'associate-profiles',
    component: AssociateProfiles,
    props: route => ({
      filters: getAssociateProfilesFilters(route.query)
    }),
    meta: {
      title: 'Personnel Information - ePeople',
      permissions: ['manage_associates']
    }
  },
  {
    path: '/leaderboard',
    name: 'leaderboard',
    component: () => import('../modules/profile/views/associate-leaderboard.vue'),
    props: route => ({
      filters: getAssociateLeaderboardFilters(route.query)
    }),
    meta: {
      title: 'Leaderboard - ePeople',
      permissions: ['manage_associates']
    }
  },
  {
    path: '/invite-history',
    name: 'invite-history',
    component: () => import('../modules/user/views/invite-history.vue'),
    meta: {
      title: 'Invitations - ePeople'
    }
  },
  {
    path: '/daily-summary',
    name: 'daily-summary',
    component: GeneralDailyReport,
    props: route => ({
      reportDate: route.query.reportDate
    }),
    meta: {
      title: 'Daily Summary - ePeople',
      permissions: ['manage_shifts']
    }
  },
  {
    path: '/activity',
    name: 'activity',
    component: () => import('../modules/profile/views/associate-activity.vue'),
    props: route => {
      return {
        filters: {
          search: route.query.search,
          position: normalizeToArray(route.query.position),
          department: normalizeToArray(route.query.department),
          agency: normalizeToArray(route.query.agency),
          payment_type: normalizeToArray(route.query.payment_type),
          work_status: normalizeToArray(route.query.work_status),
          location: route.query.location,
          task_status: normalizeToArray(route.query.task_status)
        }
      }
    },
    meta: {
      title: 'Activity - ePeople',
      permissions: ['manage_shifts']
    }
  },
  {
    path: '/time-off',
    name: 'time-off',
    component: TimeOff,
    props: route => ({
      filters: getTimeOffFilters(route.query)
    }),
    meta: {
      title: 'Time Offs - ePeople',
      permissions: ['manage_shifts']
    }
  },
  {
    path: '/shift-card-report',
    name: 'shift-card-report',
    component: () => import('../modules/report/views/report-shift-cards.vue'),
    props: true,
    meta: { title: 'Shift Card Report - ePeople' }
  },
  // {
  //   path: "/ppd-audit-report",
  //   name: "ppd-audit-report",
  //   component: () => import("../modules/report/views/report-ppd-audit.vue"),
  //   props: true,
  //   meta: { title: "PPD Audit Export Report - ePeople" },
  // },
  {
    path: '/alert-store',
    name: 'alert-store',
    component: AlertStore,
    props: route => {
      return {
        filters: {
          types: normalizeToArray(route.query.types),
          severity: route.query.severity,
          start: route.query.start,
          end: route.query.end,
          route: route.query.route
        }
      }
    },
    meta: {
      title: 'Alerts - ePeople',
      permissions: ['resolve_alerts']
    }
  },
  {
    path: '/complete-request',
    name: 'complete-request',
    component: () => import('../modules/complete-request/views/complete-request.vue'),
    props: route => {
      return {
        filters: {
          types: normalizeToArray(route.query.types),
          sequence_types: normalizeToArray(route.query.sequence_types),
          statuses: normalizeToArray(route.query.statuses)
        }
      }
    },
    meta: {
      title: 'Documentation Requests - ePeople',
      permissions: ['manage_documents']
    }
  },
  {
    path: '/documentation-page',
    name: 'documentation-page',
    component: () => import('../modules/document/views/documentation-page.vue'),
    props: true,
    redirect: { name: 'documentation-page.documents-store' },
    children: [
      {
        path: 'documents-store',
        name: 'documentation-page.documents-store',
        component: () => import('../modules/document/views/document-store.vue'),
        props: route => {
          return {
            filters: {
              type: route.query.type,
              group: route.query.group,
              initial_document: route.query.initial_document,
              with_agreement: route.query.with_agreement,
              name: route.query.name,
              source: route.query.source
            }
          }
        },
        meta: {
          title: 'Document Library - ePeople',
          permissions: ['manage_documents']
        }
      },
      {
        path: 'documents-attached',
        name: 'documentation-page.documents-attached',
        component: () => import('../modules/document/views/document-attached.vue'),
        props: route => {
          return {
            filters: {
              name: route.query.name,
              source: route.query.source,
              required_in_project: route.query.required_in_project
            }
          }
        },
        meta: {
          title: 'Attached Documents - ePeople',
          permissions: ['manage_documents']
        }
      },
      {
        path: 'in-service-hub',
        name: 'documentation-page.in-service-hub',
        component: () => import('../modules/sequence/views/sequence-hub.vue'),
        props: true,
        meta: {
          title: 'Documentation Store - ePeople',
          permissions: ['manage_documents']
        }
      },
      {
        path: 'onboarding',
        name: 'documentation-page.onboarding',
        component: () => import('../modules/sequence/views/sequence.vue'),
        props: route => {
          return {
            filters: {
              payment_type: route.query.payment_type,
              legal_entity: route.query.payment_type,
              source: route.query.source
            },
            type: 'onboarding'
          }
        },
        meta: {
          title: 'Onboarding - ePeople',
          permissions: ['manage_documents']
        }
      },
      {
        path: 'departure',
        name: 'documentation-page.departure',
        component: () => import('../modules/sequence/views/sequence.vue'),
        props: route => {
          return {
            filters: {
              payment_type: route.query.payment_type,
              legal_entity: route.query.payment_type,
              source: route.query.source
            },
            type: 'departure'
          }
        },
        meta: {
          title: 'Departure - ePeople',
          permissions: ['manage_documents']
        }
      },
      {
        path: 'orientation',
        name: 'documentation-page.orientation',
        component: () => import('../modules/sequence/views/sequence.vue'),
        props: route => {
          return {
            filters: {
              payment_type: route.query.payment_type,
              legal_entity: route.query.payment_type,
              source: route.query.source
            },
            type: 'orientation'
          }
        },
        meta: {
          title: 'Orientation - ePeople',
          permissions: ['manage_documents']
        }
      }
    ]
  },
  {
    path: '/trainings-page',
    name: 'trainings-page',
    component: () => import('../modules/sequence/views/sequence-trainings.vue'),
    props: true,
    redirect: { name: 'trainings-page.configuration' },
    children: [
      {
        path: 'configuration',
        name: 'trainings-page.configuration',
        component: () => import('../modules/sequence/views/sequence.vue'),
        props: route => {
          return {
            filters: {
              payment_type: route.query.payment_type,
              legal_entity: route.query.payment_type,
              source: route.query.source
            },
            type: 'training'
          }
        },
        meta: {
          title: 'Library - ePeople',
          permissions: ['manage_documents']
        }
      },
      {
        path: 'list',
        name: 'trainings-page.list',
        component: () => import('../modules/complete-request/views/complete-request.vue'),
        props: route => {
          return {
            filters: {
              types: ['sequence'],
              sequence_types: ['training'],
              statuses: normalizeToArray(route.query.statuses)
            }
          }
        },
        meta: {
          title: 'List - ePeople',
          permissions: ['manage_documents']
        }
      },
      {
        path: 'calendar',
        name: 'trainings-page.calendar',
        component: () => import('../modules/sequence/views/sequence-trainings-calendar.vue'),
        props: route => {
          return {}
        },
        meta: {
          title: 'Trainings Calendar - ePeople',
          permissions: ['manage_documents']
        }
      }
    ]
  },

  {
    path: '/clients',
    name: 'clients',
    component: () => import('../modules/clients/views/clients.vue'),
    meta: {
      title: 'Clients - ePeople'
    }
  },

  {
    path: '/chats',
    name: 'chats',
    component: () => import('../modules/chats/views/chat.vue'),
    meta: {
      title: 'Chats - ePeople'
    }
  },
  {
    path: '/audit-report',
    name: 'audit-report',
    component: () => import('../modules/report/views/audit-report.vue'),
    meta: {
      title: 'Audit - ePeople'
    }
  },
  {
    path: '/total-hours-report',
    name: 'total-hours-report',
    component: () => import('../modules/report/views/total-hours.vue'),
    meta: {
      title: 'Total Hours - ePeople'
    }
  },
  {
    path: '/uncompleted-critical-tasks',
    name: 'uncompleted-critical-tasks',
    component: () => import('../modules/uncompleted-critical-task/views/uncompleted-critical-task.vue'),
    props: route => {
      return {
        filters: {
          associates: normalizeToArray(route.query.associates),
          legal_entities: normalizeToArray(route.query.legal_entities),
          department_ids: normalizeToArray(route.query.department_ids),
          role_ids: normalizeToArray(route.query.role_ids)
        }
      }
    },
    meta: {
      title: 'Uncompleted Task - ePeople'
    }
  },
  {
    path: '/export-service/:name',
    name: 'export-service',
    component: () => import('../modules/report/views/export-service.vue'),
    meta: {
      title: 'Export Service - ePeople'
    }
  }
]

const router = new VueRouter({
  base: process.env.BASE_URL,
  routes
})

router.onError(error => {
  const chunkFailedPattern = /Loading chunk [\d\w]+ failed/g
  if (error.message.match(chunkFailedPattern) && navigator.onLine) {
    console.warn('Chunk load failed. Reloading page...')
    window.location.reload()
  }
})

router.beforeEach(async (to, from, next) => {
  const hasToken = store.state.applicationState.token

  // Check if the route is public
  const isPublic = to.meta && to.meta.public

  if (!hasToken && !isPublic) {
    sessionStorage.setItem('redirectPath', to.fullPath)
    return next('/auth')
  }

  document.title = to.meta?.title || 'ePeople'

  // Redirect from /auth to home if the user already has a token
  if ((to.path === '/auth' || to.path === '/sign-up') && hasToken) {
    const redirectPath = sessionStorage.getItem('redirectPath') || '/'
    sessionStorage.removeItem('redirectPath')
    return next(redirectPath)
  }

  next()
})

export default router
